import React, { useEffect, useState } from "react";
import SC from "./style";
import theme from "@theme";
import { Checkbox } from "@mui/material";
import InnoIcons from "InnoUI/innoicons";
import { folderTypesMatch } from "utils/optionData/utils";
import { Button } from "InnoUI/components";
import { apiGet, apiPost } from "api/callApi";
import { readWriteValue } from "utils/lookUpTable";
import { showAlert } from "utils/helper";

const RolePermissions = (props) => {
  const {
    onClose,
    folder,
    refreshFolders = () => {},
    selectedProjects = [],
    selectedOccupants = [],
  } = props;
  const [searchTerm, setSearchTerm] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const [assignToChildren, setAssignToChildren] = useState(false);
  const [permissionObject, setPermissionObject] = useState({});
  const [roleData, setRoleData] = useState([]);

  // function to get All Role Data
  const getRole = async () => {
    try {
      const response = await apiGet("/v1/roles?active=true", false);
      setRoleData(response.data);
    } catch (error) {
      console.log("error", error);
    }
  };

  const getPermission = async () => {
    try {
      const response = await apiGet(
        `/v1/folderpermissions/${folder.id}`,
        false
      );
      setPermissionObject(response.data.permissions);
    } catch (error) {
      console.log("error", error);
    }
  };

  useEffect(() => {
    getPermission();
    getRole();
  }, []);

  // For Sorting role Data
  const handleSort = () => {
    setSortDirection((prevDirection) =>
      prevDirection === "asc" ? "desc" : "asc"
    );
  };

  // Select role  checkbox
  const handleCheckboxChange = (roleId, permissionType) => {
    setPermissionObject((prevPermissions) => {
      const currentPermission = prevPermissions[roleId] || null;
      const newPermissions = { ...prevPermissions };

      // Toggle the selected permission and ensure mutual exclusivity
      if (currentPermission === permissionType) {
        delete newPermissions[roleId];
      } else {
        newPermissions[roleId] = permissionType;
      }

      return newPermissions;
    });
  };

  // Select and Deselect All role
  const handleSelectAll = (permissionType) => {
    setPermissionObject((prevPermissions) => {
      const newPermissions = { ...prevPermissions };
      const allSelected = roleData.every(
        (role) => newPermissions[role.id] === permissionType
      );

      // Toggle all roleData to have the specified permissionType
      roleData.forEach((role) => {
        if (allSelected) {
          delete newPermissions[role.id];
        } else {
          newPermissions[role.id] = permissionType;
        }
      });

      return newPermissions;
    });
  };

  // Api to call Add permission in  folder
  const handleSave = async () => {
    const payload = {
      permissionObject,
      assignToChildren,
      projectIds: selectedProjects,
      occupantIds: selectedOccupants,
    };
    try {
      const response = await apiPost(
        `/v1/folderpermissions/${folder.id}`,
        payload
      );
      showAlert({ title: "Success", text: response?.message, icon: "success" });
      await refreshFolders();
      onClose();
    } catch (error) {
      showAlert({
        title: "Error",
        text: error?.response?.data?.message,
        icon: "error",
      });
      console.log("Error: " + error.message);
    }
  };

  // Sort and filter
  const sortedRoles = [...roleData].sort((a, b) => {
    const roleA = a.role.toLowerCase();
    const roleB = b.role.toLowerCase();
    return sortDirection === "asc"
      ? roleA.localeCompare(roleB)
      : roleB.localeCompare(roleA);
  });

  // filter logic
  const filteredRoles = sortedRoles.filter((item) =>
    item.role.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // To show folder icon
  const matchedFolderType = folderTypesMatch.find(
    (item) => item.id === folder.folderType
  );
  const folderIcon = matchedFolderType ? matchedFolderType.icon : "📂";

  return (
    <>
      <SC.DrawerTitlesContainer>
        <SC.DrawerTitles>
          <span>{`Permission To `}</span>
          <span style={{ margin: " 6px 0px 0px 10px", color: "white" }}>
            {folderIcon}
          </span>
          <span>{folder?.name}</span>
        </SC.DrawerTitles>
        <SC.CloseIcon>
          <InnoIcons.CloseIcon onClick={onClose} />
        </SC.CloseIcon>
      </SC.DrawerTitlesContainer>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 20px 0px 20px",
        }}
      >
        <div style={{ display: "flex" }}>
          <Checkbox
            checked={assignToChildren}
            onChange={(e) => setAssignToChildren(e.target.checked)}
            sx={{
              "&.Mui-checked": {
                color: theme.colors.themeColor,
              },
            }}
            size="small"
          />
          <p style={{ fontWeight: "400" }}>Assign to all subfolders</p>
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          <Button
            caption="Cancel"
            onClick={onClose}
            fontSize="12px"
            style={{
              marginBottom: 8,
              background: theme.colors.cancelButton,
              color: theme.colors.white,
              minWidth: "90px",
              borderRadius: "3px",
              height: "30px",
              fontSize: "12px",
            }}
          />
          <Button
            caption={"Save"}
            onClick={handleSave}
            color="white"
            fontSize="12px"
            style={{
              marginBottom: 8,
              background: theme.gradient.themeGradient,
              color: theme.colors.white,
              minWidth: "90px",
              height: "30px",
              borderRadius: "3px",
            }}
          />
        </div>
      </div>
      <SC.Container>
        <SC.Table>
          <SC.Thead>
            <SC.Trheader>
              <SC.ThRole>
                Role
                <SC.InputContiner>
                  <SC.SearchIcon />
                  <SC.InputStyle
                    placeholder="Search role"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </SC.InputContiner>
                <SC.SwapVertIcon onClick={handleSort} />
              </SC.ThRole>
              <SC.Th>
                Read
                <Checkbox
                  checked={roleData.every(
                    (role) => permissionObject[role.id] === readWriteValue.read
                  )}
                  onChange={() => handleSelectAll(readWriteValue.read)}
                  sx={{
                    "&.Mui-checked": {
                      color: theme.colors.themeColor,
                    },
                  }}
                  size="small"
                />
              </SC.Th>
              <SC.Th>
                Write
                <Checkbox
                  checked={roleData.every(
                    (role) => permissionObject[role.id] === readWriteValue.write
                  )}
                  onChange={() => handleSelectAll(readWriteValue.write)}
                  sx={{
                    "&.Mui-checked": {
                      color: theme.colors.themeColor,
                    },
                  }}
                  size="small"
                />
              </SC.Th>
            </SC.Trheader>
          </SC.Thead>
          <SC.Tbody>
            {filteredRoles.map((item) => (
              <SC.Tr key={item.id}>
                <SC.TdRole>{item.role}</SC.TdRole>
                <SC.Td>
                  <Checkbox
                    checked={permissionObject[item.id] === readWriteValue.read}
                    onChange={() =>
                      handleCheckboxChange(item.id, readWriteValue.read)
                    }
                    sx={{
                      "&.Mui-checked": {
                        color: theme.colors.themeColor,
                      },
                    }}
                    size="small"
                  />
                </SC.Td>
                <SC.Td>
                  <Checkbox
                    checked={permissionObject[item.id] === readWriteValue.write}
                    onChange={() =>
                      handleCheckboxChange(item.id, readWriteValue.write)
                    }
                    sx={{
                      "&.Mui-checked": {
                        color: theme.colors.themeColor,
                      },
                    }}
                    size="small"
                  />
                </SC.Td>
              </SC.Tr>
            ))}
          </SC.Tbody>
        </SC.Table>
      </SC.Container>
    </>
  );
};

export default RolePermissions;
